import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { tv } from 'tailwind-variants';
import { cn } from '../../utils/cn.esm.js';

// Spinner.styles.tsx
const spinnerStyles = tv({
    slots: {
        base: `
      iris:inline-flex
      iris:items-center
      iris:justify-center
    `,
        trackDark: "",
        trackLight: "",
        track: "",
        spinnerArc: "",
        standardMotionArc: `
      iris:motion-safe:block 
      iris:motion-reduce:hidden
    `,
        reducedMotionArc: `
      iris:motion-safe:hidden 
      iris:motion-reduce:block
    `,
    },
    variants: {
        size: {
            sm: {
                base: "iris:size-(--comp-size-box-spinner-sm)",
            },
            md: {
                base: "iris:size-(--comp-size-box-spinner-md)",
            },
            lg: {
                base: "iris:size-(--comp-size-box-spinner-lg)",
            },
            xl: {
                base: "iris:size-(--comp-size-box-spinner-xl)",
            },
        },
        // Responsive size variants
        smSize: {
            sm: {
                base: "iris:sm:size-(--comp-size-box-spinner-sm)",
            },
            md: {
                base: "iris:sm:size-(--comp-size-box-spinner-md)",
            },
            lg: {
                base: "iris:sm:size-(--comp-size-box-spinner-lg)",
            },
            xl: {
                base: "iris:sm:size-(--comp-size-box-spinner-xl)",
            },
        },
        mdSize: {
            sm: {
                base: "iris:md:size-(--comp-size-box-spinner-sm)",
            },
            md: {
                base: "iris:md:size-(--comp-size-box-spinner-md)",
            },
            lg: {
                base: "iris:md:size-(--comp-size-box-spinner-lg)",
            },
            xl: {
                base: "iris:md:size-(--comp-size-box-spinner-xl)",
            },
        },
        lgSize: {
            sm: {
                base: "iris:lg:size-(--comp-size-box-spinner-sm)",
            },
            md: {
                base: "iris:lg:size-(--comp-size-box-spinner-md)",
            },
            lg: {
                base: "iris:lg:size-(--comp-size-box-spinner-lg)",
            },
            xl: {
                base: "iris:lg:size-(--comp-size-box-spinner-xl)",
            },
        },
        inverse: {
            true: {
                trackDark: "iris:stroke-(--comp-color-background-spinner-dark)",
                trackLight: "iris:stroke-(--comp-color-background-spinner-light)",
                spinnerArc: "iris:stroke-(--comp-color-content-spinner-arc-inverse)",
            },
            false: {
                trackDark: "iris:stroke-(--comp-color-background-spinner-dark)",
                trackLight: "iris:stroke-(--comp-color-background-spinner-light)",
                spinnerArc: "iris:stroke-(--comp-color-content-spinner-arc)",
            },
        },
    },
    defaultVariants: {
        size: "md",
        inverse: false,
    },
});

/**
 * Spinner component for indicating loading states in various contexts
 */
const Spinner = (_a) => {
    var { className = "", size = "md", inverse = false, message = "Loading content, please wait.", "aria-busy": ariaBusy = true, arcClassName = "" } = _a, props = __rest(_a, ["className", "size", "inverse", "message", "aria-busy", "arcClassName"]);
    const styleOptions = {
        size: typeof size === "string" ? size : (size === null || size === void 0 ? void 0 : size.xs) || undefined,
        smSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.sm : undefined,
        mdSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.md : undefined,
        lgSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.lg : undefined,
        inverse,
        class: className,
    };
    const { base, trackDark, trackLight, spinnerArc, standardMotionArc, reducedMotionArc, } = spinnerStyles(styleOptions);
    return (jsxs("div", Object.assign({ className: cn(base(), className), role: "status", "aria-live": "polite" }, props, { children: [jsx("span", { className: "iris:sr-only", children: message }), jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 64 64", preserveAspectRatio: "xMidYMid", className: "iris:h-full iris:w-full", "aria-hidden": "true", children: [jsx("circle", { cx: "32", cy: "32", r: "24", className: trackDark(), strokeWidth: "8", fill: "transparent" }), jsx("circle", { cx: "32", cy: "32", r: "24", className: trackLight(), strokeWidth: "8", fill: "transparent" }), jsxs("circle", { cx: "32", cy: "32", r: "24", className: cn(spinnerArc(), standardMotionArc(), arcClassName), strokeWidth: "8", strokeLinecap: "round", fill: "transparent", children: [jsx("animateTransform", { attributeName: "transform", type: "rotate", repeatCount: "indefinite", dur: "1.5s", values: "0 32 32;180 32 32;720 32 32", keyTimes: "0;0.5;1" }), jsx("animate", { attributeName: "stroke-dasharray", repeatCount: "indefinite", dur: "1.5s", values: "12.566370614359172 238.76104167282426;128.4955592153876 62.83185307179585;12.566370614359172 238.76104167282426", keyTimes: "0;0.5;1" })] }), jsxs("circle", { cx: "32", cy: "32", r: "24", className: cn(spinnerArc(), reducedMotionArc(), arcClassName), strokeWidth: "8", strokeLinecap: "round", fill: "transparent", children: [jsx("animateTransform", { attributeName: "transform", type: "rotate", repeatCount: "indefinite", dur: "1.5384615384615383s", values: "0 32 32;360 32 32", keyTimes: "0;1" }), jsx("animate", { attributeName: "stroke-dasharray", repeatCount: "indefinite", dur: "1.5s", values: "72.4955592153876 48.83185307179585;72.4955592153876 48.83185307179585", keyTimes: "0;1" })] })] })] })));
};
Spinner.displayName = "Spinner";

export { Spinner };
